import { getJWTToken } from '../../utils/auth';
import { authHeaders } from '../../core/impersonation';
import type {
  BenchmarkMonitoring,
  MonitoringBenchmark,
  MonitoringGroup,
  MonitoringRef,
  MonitoringStudent,
  MonitoringTeacher,
  MonitoringWindow,
} from './types';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export class BenchmarkMonitoringNotFound extends Error {
  constructor() {
    super('benchmark monitoring: not found');
    this.name = 'BenchmarkMonitoringNotFound';
  }
}

export interface MonitoringQuery {
  school?: string;
  teacher?: string;
}

const LEVELS = ['district', 'school', 'teacher'];
const SCOPES = ['district', 'school'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asList<T>(value: unknown, what: string): T[] | undefined {
  if (value === undefined || value === null) return undefined;
  if (!Array.isArray(value)) throw new Error(`benchmark monitoring response has a bad ${what}`);
  return value as T[];
}

function asRef(value: unknown): MonitoringRef | undefined {
  if (!isRecord(value)) return undefined;
  return { id: String(value.id ?? ''), name: String(value.name ?? '') };
}

function asTeacher(value: unknown): MonitoringTeacher | undefined {
  if (!isRecord(value)) return undefined;
  return {
    id: String(value.id ?? ''),
    name: String(value.name ?? ''),
    firstName: String(value.firstName ?? ''),
    email: String(value.email ?? ''),
  };
}

function asWindow(value: unknown): MonitoringWindow | null {
  if (value === null || value === undefined) return null;
  if (!isRecord(value) || typeof value.closeAt !== 'string' || typeof value.startAt !== 'string') {
    throw new Error('benchmark monitoring response has a bad window');
  }
  return {
    type: String(value.type ?? ''),
    label: String(value.label ?? ''),
    state: value.state === 'open' ? 'open' : 'closed',
    startAt: value.startAt,
    closeAt: value.closeAt,
    flashlightScored: value.flashlightScored === true,
  };
}

function asMonitoring(body: unknown): BenchmarkMonitoring {
  if (!isRecord(body)) throw new Error('benchmark monitoring response is empty');
  if (!SCOPES.includes(String(body.scope)) || !LEVELS.includes(String(body.level))) {
    throw new Error('benchmark monitoring response has an unknown scope or level');
  }
  if (typeof body.students !== 'number') throw new Error('benchmark monitoring response has no student count');
  const benchmarks = asList<MonitoringBenchmark>(body.benchmarks, 'benchmarks');
  if (!benchmarks) throw new Error('benchmark monitoring response has no benchmarks');
  return {
    scope: body.scope as BenchmarkMonitoring['scope'],
    level: body.level as BenchmarkMonitoring['level'],
    districtName: typeof body.districtName === 'string' ? body.districtName : '',
    schoolYear: typeof body.schoolYear === 'string' ? body.schoolYear : '',
    window: asWindow(body.window),
    upNext: asList<BenchmarkMonitoring['upNext'][number]>(body.upNext, 'upNext') ?? [],
    school: asRef(body.school),
    teacher: asTeacher(body.teacher),
    students: body.students,
    benchmarks,
    schools: asList<MonitoringGroup>(body.schools, 'schools'),
    teachers: asList<MonitoringGroup>(body.teachers, 'teachers'),
    studentRows: asList<MonitoringStudent>(body.studentRows, 'studentRows'),
    contactEmails: asList<string>(body.contactEmails, 'contactEmails') ?? [],
    ...(typeof body.refreshedAt === 'string' ? { refreshedAt: body.refreshedAt } : {}),
  };
}

// The viewer's calendar date decides which school year the server reads;
// their July 1 may still be June 30 in UTC.
export function localDate(now: Date): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export async function fetchBenchmarkMonitoring(query: MonitoringQuery = {}, now: Date = new Date()): Promise<BenchmarkMonitoring> {
  const token = await getJWTToken();
  const params = new URLSearchParams();
  if (query.school) params.set('school', query.school);
  if (query.teacher) params.set('teacher', query.teacher);
  params.set('date', localDate(now));
  const search = params.toString();
  const response = await fetch(`${apiUrl}/benchmark-monitoring${search ? `?${search}` : ''}`, {
    headers: authHeaders(token),
  });
  if (response.status === 404) {
    throw new BenchmarkMonitoringNotFound();
  }
  if (!response.ok) {
    throw new Error(`benchmark monitoring fetch failed: ${response.status}`);
  }
  return asMonitoring(await response.json());
}
